import { motion } from "framer-motion";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { courses as localCourses } from "../data/courses";
import { API_URL } from "../apiConfig";



// Register GSAP plugins
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

export default function CoursesPage() {
  const [expandedCourses, setExpandedCourses] = useState(new Set());
  const courses = localCourses;
  const [courseFees, setCourseFees] = useState({});

  useEffect(() => {
    fetch(API_URL)
      .then(res => res.json())
      .then(data => {
        const profiles = Array.isArray(data) ? data : data.data || [];
        const fees = {};
        profiles.forEach(p => {
          const title = (p.course_title || p.name || "").toLowerCase();
          if (title.includes("admin fee") || title.includes("adminfee")) {
            return;
          }
          const fee = parseFloat(p.course_fee || 0);
          if (title.includes("scaffold")) {
            if (title.includes("level 2") || title.includes("l2")) {
              fees["101_level2"] = fee;
            } else {
              fees["101_level1"] = fee;
            }
          } else if (title.includes("welding")) {
            if (title.includes("level 2") || title.includes("l2")) {
              fees["102_level2"] = fee;
            } else {
              fees["102_level1"] = fee;
            }
          } else if (title.includes("electrical")) {
            if (title.includes("level 2") || title.includes("l2")) {
              fees["103_level2"] = fee;
            } else {
              fees["103_level1"] = fee;
            }
          } else if (title.includes("rigging") || title.includes("signalling")) {
            if (title.includes("level 2") || title.includes("l2")) {
              fees["104_level2"] = fee;
            } else {
              fees["104_level1"] = fee;
            }
          }
        });
        setCourseFees(fees);
      })
      .catch(err => console.error("Error fetching course profiles:", err));
  }, []);


  const toggleReadMore = (courseId) => {
    setExpandedCourses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(courseId)) {
        newSet.delete(courseId);
      } else {
        newSet.add(courseId);
      }
      return newSet;
    });
  };

  return (
  <div className="bg-white text-gray-900 font-sans overflow-x-hidden scroll-smooth">
    <Navbar />

    {/* GREEN WRAPPER - Covers navbar space + marquee seamlessly */}
    <div className="bg-[#0b9949]">
      {/* Spacer for fixed navbar */}
      {/* Spacer for fixed navbar */}
<div className="h-[130px] md:h-[175px] lg:h-[120px]"></div>

      
      {/* 🏃‍♂️ MARQUEE SECTION */}
      <div className="relative overflow-hidden">
        {/* Fade edges */}
        <div className="absolute left-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-r from-[#0b9949] to-transparent z-10"></div>
        <div className="absolute right-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-l from-[#0b9949] to-transparent z-10"></div>
        
        <div className="py-3 md:py-4">
          <div className="flex animate-scroll">
            {[...courses, ...courses, ...courses].map((course, index) => (
              <Link
                to={`/course-details/${course.id}`}
                key={`marquee-${index}`}

                className="flex-shrink-0 mx-2 md:mx-4 group"
              >
                <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 md:px-6 py-2 border border-white/20 hover:bg-white/25 hover:scale-105 transition-all duration-300 cursor-pointer">
                  <span className="w-2 h-2 bg-yellow-400 rounded-full mr-2 md:mr-3 group-hover:animate-pulse"></span>
                  <span className="text-white font-medium text-xs md:text-sm whitespace-nowrap group-hover:text-yellow-200 transition-colors">
                    {course.title}
                  </span>
                  <svg 
                    className="w-3 h-3 md:w-4 md:h-4 ml-2 text-white/60 group-hover:text-yellow-400 group-hover:translate-x-1 transition-all" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>

    <style jsx>{`
      @keyframes scroll {
        0% { transform: translateX(0); }
        100% { transform: translateX(-33.33%); }
      }
      .animate-scroll {
        animation: scroll 30s linear infinite;
        width: max-content;
      }
      .animate-scroll:hover {
        animation-play-state: paused;
      }
    `}</style>

    {/* 🌊 HERO SECTION - No gap now */}
    <div className="relative min-h-screen bg-gradient-to-b from-white to-green-50 overflow-hidden">
      <div
        className="relative h-[400px] bg-fixed bg-center bg-cover"
        style={{
          backgroundImage:
            "url('./aboutpage.avif')",
        }}
      >
          <div className="absolute inset-0 bg-[#0b9949]/70 mix-blend-multiply" />

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="absolute inset-0 flex flex-col items-center justify-center text-center text-white px-4"
          >
        <h1 className="text-4xl md:text-5xl font-extrabold drop-shadow-lg">
  Explore Our Expert-Led Training Courses
</h1>
<p className="mt-4 text-lg text-green-100 max-w-2xl">
  Build your professional foundation with certified programs designed for real-world excellence and career advancement.
</p>

          </motion.div>
        </div>

        {/* ✔ INTRO SECTION */}
        {/* <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto text-center py-16 px-6"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-[#0b9949] mb-4">
            Why Choose Our Courses?
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Our programs are designed for professionals, teams, and organizations who want excellence in safety performance. Gain real-world knowledge, WSQ-accredited certifications, and employer-ready skills.
          </p>
          <img
            src="https://images.unsplash.com/photo-1581090700227-1e37b190418e?auto=format&fit=crop&w=800&q=80"
            className="rounded-xl mx-auto shadow-lg w-full max-w-xl"
            alt="Training"
          />
        </motion.div> */}

        {/* 🟢 COURSES GRID */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-16">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-14"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#0b9949]">
              Transform Your Learning Experience
            </h2>
            <p className="text-gray-600 mt-3">
              Choose from expert-led training programs trusted by companies nationwide.
            </p>
          </motion.div>

          <div className="grid gap-8 max-w-6xl mx-auto grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {courses.map((course, i) => {
              const startingPrice = courseFees[`${course.id}_level1`] || course.levels.level1.price;

              return (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0, y: 60 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.15, duration: 0.7 }}
                  whileHover={{ scale: 1.05 }}
                  className="bg-white rounded-xl shadow-md p-5 flex flex-col hover:shadow-xl transition min-h-[340px]"
                >
                  <div className="relative">
                    <img
                      src={course.bannerImage}
                      className="rounded-lg object-cover h-40 w-full mb-4"
                      alt={course.title}
                    />
                  </div>

                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-green-100 text-green-700 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider">
                      Level 1
                    </span>

                  </div>

                  <h3 className="text-green-800 font-semibold text-lg mb-2">
                    {course.title}
                  </h3>

                  <div className="text-gray-700 text-sm flex-grow">
                    {expandedCourses.has(course.id) ? (
                      <>
                        {course.overview}
                        <button
                          onClick={() => toggleReadMore(course.id)}
                          className="text-green-600 hover:text-green-800 font-medium ml-1"
                        >
                          Show Less
                        </button>
                      </>
                    ) : (
                      <>
                        {course.overview.length > 100
                          ? `${course.overview.substring(0, 100)}...`
                          : course.overview}
                        {course.overview.length > 100 && (
                          <button
                            onClick={() => toggleReadMore(course.id)}
                            className="text-green-600 hover:text-green-800 font-medium ml-1"
                          >
                            Read More
                          </button>
                        )}
                      </>
                    )}
                  </div>

                  <p className="mt-3 text-sm font-medium text-green-700">
                    💰 From <span className="font-bold">${startingPrice}</span>
                  </p>

                  <Link to={`/course-details/${course.id}`}>
                    <motion.button
                      whileHover={{ scale: 1.06 }}
                      className="mt-4 bg-[#0b9949] text-white w-full py-2 rounded-lg font-medium"
                    >
                      View Details
                    </motion.button>
                  </Link>

                </motion.div>
              );
            })}
          </div>
        </div>

        {/* ⭐ STATS SECTION */}
        {/* <section className="py-16 px-6 bg-[#f6fdf9] text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0b9949] mb-12">
            Achieve More with Our Programs
          </h2>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              { number: "20+", label: "Years Experience" },
              { number: "50+", label: "WSQ Programs" },
              { number: "10k+", label: "Trainees Certified" },
              { number: "100%", label: "Government Approved" },
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: i * 0.15 }}
                className="bg-white rounded-2xl p-8 shadow-md hover:shadow-lg transition"
              >
                <h3 className="text-4xl font-bold text-[#0b9949] mb-2">
                  {stat.number}
                </h3>
                <p className="text-gray-600">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </section> */}

        {/* 🚨 WHY ATTEND SECTION */}
        <section className="py-20 px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl md:text-5xl font-extrabold text-[#0b9949] mb-6">
                Why Attend?
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Left Column - Why Attend Points */}
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
                className="bg-gradient-to-br from-green-50 to-white rounded-2xl p-8 shadow-lg"
              >
                <ul className="space-y-4 text-gray-800">
                  <li className="flex items-start">
                    <span className="text-green-600 font-bold mr-3">•</span>
                    <span><strong>Mandatory</strong> for workers in the Process, Construction, and Maintenance (PCM) sectors.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 font-bold mr-3">•</span>
                    <span>Meet <strong>compliance requirements</strong>.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 font-bold mr-3">•</span>
                    <span>Avoid <strong>High Levy</strong> and project delays.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 font-bold mr-3">•</span>
                    <span>Equip workers with <strong>essential Skills and knowledge</strong>.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 font-bold mr-3">•</span>
                    <span>Enhance <strong>job readiness and employability</strong>.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 font-bold mr-3">•</span>
                    <span>Incentives for early adoption of the <strong>Enhanced R1 courses</strong>.</span>
                  </li>
                </ul>
              </motion.div>

              {/* Right Column - Worker Image */}
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
                className="flex items-center justify-center"
              >
                <div className="relative">
                  <div className="absolute inset-0 bg-[#0b9949] rounded-full blur-3xl opacity-20"></div>
                  <img
                    src="./Attend.avif"
                    alt="PCM Worker"
                    className="relative rounded-2xl shadow-2xl w-full max-w-md"
                  />
                </div>
              </motion.div>
            </div>

            {/* Enhanced R1 Framework Table */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white rounded-2xl shadow-xl p-8 border-2 border-green-100"
            >
              <h3 className="text-2xl md:text-3xl font-bold text-[#0b9949] mb-6">
                Enhanced R1 Framework – Requirements:
              </h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Get the R1 levy rate, existing workers in the Process Sector must meet{" "}
                <strong className="text-green-700">one of these criteria by 31 December 2025:</strong>
              </p>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-[#0b9949] text-white">
                      <th className="border border-gray-300 p-4 text-left font-semibold">
                        Revised Pathway
                      </th>
                      <th className="border border-gray-300 p-4 text-left font-semibold">
                        Enhanced R1 Framework
                      </th>
                      <th className="border border-gray-300 p-4 text-left font-semibold">
                        Requirements
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-green-50">
                      <td className="border border-gray-300 p-4 font-semibold text-center">1</td>
                      <td className="border border-gray-300 p-4">
                        <strong>Skills Qualifications (4 courses)</strong>
                        <ul className="mt-2 space-y-1 text-sm">
                          <li>• 2 x Level 1 WSQ courses* (L1 WSQ)</li>
                          <li>• 1 x Level 2 Deep-skilling course* (L2 CoC)</li>
                          <li>• 1 x Safety course</li>
                        </ul>
                      </td>
                      <td className="border border-gray-300 p-4">
                        <ul className="space-y-2 text-sm">
                          <li><strong>+</strong> Fixed Monthly Salary (FMS) ≥ $1,200</li>
                          <li><strong>+</strong> Period of Employment (POE) ≥ 4 years</li>
                        </ul>
                      </td>
                    </tr>
                    <tr className="bg-white">
                      <td className="border border-gray-300 p-4 font-semibold text-center">2</td>
                      <td className="border border-gray-300 p-4 text-sm">
                        <strong>Productivity Certification Framework (PCF)</strong> Rating of at least Bronze
                      </td>
                      <td className="border border-gray-300 p-4 text-sm">
                        <strong>+</strong> All R1 WPHs will be required to undergo Proficiency Test and Refreshers (PT&R) for the L1 and L2 courses every 2 years to retain their R1 status.
                      </td>
                    </tr>
                    <tr className="bg-green-50">
                      <td className="border border-gray-300 p-4 font-semibold text-center">3</td>
                      <td className="border border-gray-300 p-4 text-sm">
                        All R1 WPHs will be required to undergo Proficiency Test and Refreshers (PT&R) for the L1 and L2 courses every 2 years to retain their R1 status.
                      </td>
                      <td className="border border-gray-300 p-4"></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mt-8 grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 p-6 rounded-xl">
                  <h4 className="font-bold text-green-800 mb-3">📋 Key Notes:</h4>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• Skills Qualifications will be raised from 2 courses to 4 courses.</li>
                    <li>• Composite Assessment for Generic Manufacturing (Process) will be an alternative pathway to attain one Level 1 WSQ skill set.</li>
                    <li>• Introduction of Level 2 Deep-Skilling course.</li>
                  </ul>
                </div>

                <div className="bg-green-50 p-6 rounded-xl">
                  <h4 className="font-bold text-green-800 mb-3">✅ Important Updates:</h4>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• Introduction of safety course: The safety course is once-off and there is no refresher required.</li>
                    <li>• All R1 WPHs will be required to undergo Proficiency Test and Refreshers (PT&R) for the Level 1 WSQ courses and Level 2 Deep-skilling course every 2 years to retain their R1 status.</li>
                  </ul>
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="mt-6 text-center"
              >
                <a
                  href="https://www.aspri.sg"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-700 font-semibold underline hover:text-green-900"
                >
                  • Visit ASPRI Website for more details.
                </a>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* 👥 WHO SHOULD ATTEND SECTION */}
        <section className="py-20 px-6 bg-gradient-to-b from-green-50 to-white">
          <div className="max-w-6xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="text-4xl md:text-5xl font-extrabold text-[#0b9949] text-center mb-12"
            >
              Who Should Attend?
            </motion.h2>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white rounded-2xl shadow-xl p-8 md:p-12"
            >
              <p className="text-gray-800 text-lg leading-relaxed">
                <strong>New and existing workers</strong> in{" "}
                <span className="text-green-700 font-bold">
                  Process, Construction, and Maintenance Industries PCM
                </span>
                , including activities involved Mechanical & Electrical, Civil & Structural, 
                Cleaning & Maintenance, Safety & Inspection, or any related PCM work sectors.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 💎 COURSE BENEFITS SECTION */}
        <section className="py-20 px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="text-4xl md:text-5xl font-extrabold text-green-600 text-center mb-12"
            >
              Course Benefits
            </motion.h2>

            <div className="grid gap-8 max-w-2xl mx-auto">
              {[
                {
                  number: "1",
                  title: "Lower Foreign Worker Levy",
                  points: [
                    "Companies that employ workers under the R1 (Higher Skilled) category enjoy reduced levy costs compared to Basic Skilled (R2) workers.",
                    "This translates into substantial cost savings over time, especially for companies with large workforces.",
                  ],
                },
                {
                  number: "2",
                  title: "Enhanced Productivity",
                  points: [
                    "R1 workers are more skilled and competent, often having undergone WSQ-accredited training.",
                    "They require less supervision, contribute to higher work quality, and fewer reworks and delays.",
                  ],
                },
                {
                  number: "3",
                  title: "Better Worksite Safety & Compliance",
                  points: [
                    "R1 workers are trained in Workplace Safety and Health (WSH), reducing the risk of accidents.",
                    "This supports compliance with MOM and WSH Council requirements, minimizing regulatory issues.",
                  ],
                },
                {
                  number: "4",
                  title: "Alignment with Government Incentives",
                  points: [
                    "Companies that invest in training and upgrading workers to R1 are more likely to qualify for government grants or support schemes.",
                    "(e.g., SSG Funding, PSG-Jobs).",
                    "This aligns with Singapore's national productivity and skills upgrading objectives.",
                  ],
                },
                {
                  number: "5",
                  title: "Supports Long-Term Business Sustainability",
                  points: [
                    "R1 framework helps build a more competent and resilient workforce.",
                    "Enhances a company's ability to scale sustainably and handle complex projects.",
                  ],
                },
              ].map((benefit, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: i * 0.1 }}
                  className="bg-gradient-to-br from-green-50 to-white rounded-2xl p-8 shadow-lg border-2 border-green-100 hover:shadow-xl transition"
                >
                  <div className="flex items-start mb-4">
                    <div className="bg-[#0b9949] text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg mr-4 flex-shrink-0">
                      {benefit.number}
                    </div>
                    <h3 className="text-xl font-bold text-green-800">
                      {benefit.title}
                    </h3>
                  </div>
                  <ul className="space-y-3 text-gray-700">
                    {benefit.points.map((point, j) => (
                      <li key={j} className="flex items-start">
                        <span className="text-green-600 mr-2 mt-1">•</span>
                        <span className="text-sm leading-relaxed">{point}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 💰 PROCESS WORK PERMIT LEVY TABLE */}
        <section className="py-20 px-6 bg-gradient-to-b from-green-50 to-white">
          <div className="max-w-5xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="text-4xl md:text-5xl font-extrabold text-green-600 text-center mb-12"
            >
              Process Work Permit Levy
            </motion.h2>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white rounded-2xl shadow-2xl overflow-hidden"
            >
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#0b9949] text-white">
                      <th className="p-4 text-left font-bold">Skill Level</th>
                      <th className="p-4 text-left font-bold">Source Country/Region</th>
                      <th className="p-4 text-left font-bold">Monthly Levy</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-green-50 border-b border-gray-200">
                      <td className="p-4 font-semibold">Higher-skilled (R1)</td>
                      <td className="p-4">Malaysia, North Asian Sources (NAS), PRC</td>
                      <td className="p-4 font-bold text-green-700">$ 200</td>
                    </tr>
                    <tr className="bg-white border-b border-gray-200">
                      <td className="p-4 font-semibold">Higher-skilled (R1)</td>
                      <td className="p-4">Non-Traditional Sources (NTS)</td>
                      <td className="p-4 font-bold text-green-700">$ 300</td>
                    </tr>
                    <tr className="bg-green-50 border-b border-gray-200">
                      <td className="p-4 font-semibold">Basic-skilled (R2)</td>
                      <td className="p-4">Malaysia, North Asian Sources (NAS), PRC</td>
                      <td className="p-4 font-bold text-green-600">$ 450</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="p-4 font-semibold">Basic-skilled (R2)</td>
                      <td className="p-4">Non-Traditional Sources</td>
                      <td className="p-4 font-bold text-green-600">$ 650</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-gradient-to-r from-green-100 to-green-50 p-6 text-center">
                {/* <p className="text-gray-800 font-semibold">
                  💡 <span className="text-green-700">Save up to $450/month per worker</span> by upgrading from R2 to R1 status!
                </p> */}
              </div>
            </motion.div>
          </div>
        </section>

        {/* 💥 ORIGINAL BENEFIT SECTION */}
        <section className="py-20 px-6 bg-white text-center">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-3xl md:text-4xl font-extrabold text-[#0b9949] mb-8"
          >
            Why Thousands Choose Our PCM Courses
          </motion.h2>

          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8 text-left">
            {[
              {
                title: "APPROVED CERTIFICATION",
                desc: "Meets Singapore PCM R1 legal requirements and grants official recognized certificates.",
              },
              {
                title: "HIGH DEMAND JOB SKILLS",
                desc: "Required by Oil & Gas, Marine, Construction, Manufacturing, Process Plants.",
              },
              {
                title: "BETTER SALARY & JOB SECURITY",
                desc: "Certified workers earn more and qualify for higher contract roles.",
              },
              {
                title: "PCM LEVY COMPLIANCE",
                desc: "Mandatory for employers to avoid levy penalties under.",
              },
              {
                title: "HANDS-ON PRACTICAL TRAINING",
                desc: "70% practical learning — real tools, real scenarios, real competency.",
              },
              {
                title: "FREE RE-ASSESSMENT SUPPORT",
                desc: "If you fail the first attempt, we guide you until you pass successfully.",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: i % 2 === 0 ? -40 : 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-white shadow-md hover:shadow-xl rounded-2xl p-6 border border-gray-100"
              >
                <h3 className="font-bold text-green-700 text-lg">{item.title}</h3>
                <p className="text-gray-600 mt-2 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-10 font-semibold text-lg text-gray-800"
          >
            <span className="text-[#0b9949] font-bold">BONUS:</span> Digital Certificate + Hard Copy Included at No Extra Cost
          </motion.p> */}
        </section>
      </div>

      <Footer />
    </div>
  );
}